'use client';

import {
  createContext,
  type Dispatch,
  ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { PromiseDataObj } from './types';

type ContextValue = {
  promiseDataObjects: PromiseDataObj[];
  setPromiseDataObjects: Dispatch<SetStateAction<PromiseDataObj[]>>;
  getPromiseDataObject: (
    topLevelQueryKey: string,
  ) => PromiseDataObj | undefined;
};

const PromiseDataContext = createContext<ContextValue | undefined>(undefined);

export function PromiseDataProvider({
  children,
  initialPromiseDataObjects,
}: {
  children: ReactNode;
  initialPromiseDataObjects: PromiseDataObj[];
}) {
  const [promiseDataObjects, setPromiseDataObjects] = useState<
    PromiseDataObj[]
  >(initialPromiseDataObjects);

  const getPromiseDataObject = useCallback((topLevelQueryKey: string) => {
    return promiseDataObjects.find(
      (promiseDataObj) => promiseDataObj?.topLevelQueryKey === topLevelQueryKey,
    );
  }, []);

  const value: ContextValue = useMemo(
    () => ({
      promiseDataObjects,
      setPromiseDataObjects,
      getPromiseDataObject,
    }),
    [],
  );

  return (
    <PromiseDataContext.Provider value={value}>
      {children}
    </PromiseDataContext.Provider>
  );
}

export function usePromiseDataContext() {
  const context = useContext(PromiseDataContext);

  return context;
}
